import { NetworkStatus } from '@apollo/client';

/** Max rows fetched when search is active (backend has no search filter). */
export const PORTFOLIO_SEARCH_FETCH_SIZE = 500;

/** True while Apollo is applying new variables or fetching the active page. */
export function isPortfolioPageLoading(networkStatus: NetworkStatus): boolean {
  return (
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables ||
    networkStatus === NetworkStatus.refetch
  );
}

/** Only trust `fetchPortfolioReports` rows once the active request has settled. */
export function isPortfolioPageReady(networkStatus: NetworkStatus): boolean {
  return networkStatus === NetworkStatus.ready;
}

export function getServerPagination({
  page,
  pageSize,
  isClientSearch,
}: {
  page: number;
  pageSize: number;
  isClientSearch: boolean;
}): { perPage: number; offSet: number } {
  if (isClientSearch) {
    return { perPage: PORTFOLIO_SEARCH_FETCH_SIZE, offSet: 0 };
  }
  return { perPage: pageSize, offSet: (page - 1) * pageSize };
}

/** Stable key for the active server page request (offSet + perPage + search mode). */
export function getPaginationRequestKey({
  page,
  pageSize,
  isClientSearch,
}: {
  page: number;
  pageSize: number;
  isClientSearch: boolean;
}): string {
  const { offSet, perPage } = getServerPagination({
    page,
    pageSize,
    isClientSearch,
  });
  return `${offSet}:${perPage}:${isClientSearch ? 'search' : 'server'}`;
}

/**
 * Rows are only safe to render once the latest `ready` transition completed for
 * the current request key. `networkStatus === ready` alone is not enough — on the
 * render where `page` changes, Apollo may still report `ready` from the prior page
 * while `data` is already cleared.
 */
export function isPaginationSettled(
  requestKey: string,
  settledKey: string | null,
): boolean {
  return settledKey === requestKey;
}

export function sliceClientPage<T>(
  items: readonly T[],
  page: number,
  pageSize: number,
): T[] {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export function getPageCount(totalCount: number, pageSize: number): number {
  if (totalCount <= 0 || pageSize <= 0) return 1;
  return Math.ceil(totalCount / pageSize);
}
